import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { api, errMsg, TELEGRAM_URL } from "@/lib/api";
import { toast } from "sonner";

const KEY = "gamerealm_chat";
const load = () => { try { return JSON.parse(localStorage.getItem(KEY) || localStorage.getItem("nexuskeys_chat")); } catch { return null; } };

export const Bubble = ({ m, mine }) => (
  <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
    <div className={`max-w-[80%] px-3 py-2 rounded-lg text-sm whitespace-pre-wrap break-words ${mine ? "bg-emerald-500 text-[#08090C]" : "bg-[#1c2030] text-gray-100"}`}>
      {m.text}
      <span className={`block text-[10px] mt-1 ${mine ? "text-[#08090C]/60" : "text-gray-500"}`}>{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
    </div>
  </div>
);

export const usePolledMessages = (fetcher, key) => {
  const [msgs, setMsgs] = useState([]);
  const last = useRef(null);
  useEffect(() => {
    setMsgs([]); last.current = null;
    if (!key) return undefined;
    let alive = true;
    const tick = async () => {
      try {
        const data = await fetcher(last.current);
        if (alive && data.length) { last.current = data[data.length - 1].created_at; setMsgs((p) => [...p, ...data.filter((d) => !p.some((x) => x.id === d.id))]); }
      } catch {}
    };
    tick();
    const t = setInterval(tick, 4000);
    return () => { alive = false; clearInterval(t); };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const push = (m) => { last.current = m.created_at; setMsgs((p) => [...p, m]); };
  return [msgs, push];
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  const payPage = pathname.startsWith("/checkout") || pathname.startsWith("/complete");
  const { open: cartOpen } = useCart();
  const [chat, setChat] = useState(load);
  const [text, setText] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const endRef = useRef(null);
  const [msgs, push] = usePolledMessages(
    (since) => api.get(`/chat/${chat.chat_id}/messages`, { params: { token: chat.token, ...(since ? { since } : {}) } }).then((r) => r.data),
    open && chat ? chat.chat_id : null,
  );
  useEffect(() => { const el = endRef.current?.parentElement; if (el) el.scrollTop = el.scrollHeight; }, [msgs, open]);
  useEffect(() => {
    const onOpen = (e) => { setOpen(true); if (e.detail?.text) setText(e.detail.text); };
    window.addEventListener("open-chat", onOpen);
    return () => window.removeEventListener("open-chat", onOpen);
  }, []);

  const [starting, setStarting] = useState(false);
  const begin = async (e) => {
    e.preventDefault();
    setStarting(true);
    try {
      const r = await api.post("/chat/start", { name, email });
      localStorage.setItem(KEY, JSON.stringify(r.data));
      setChat(r.data);
    } catch (err) { toast.error(errMsg(err, "Could not start chat")); }
    finally { setStarting(false); }
  };
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      const r = await api.post(`/chat/${chat.chat_id}/messages`, { token: chat.token, text });
      push(r.data); setText("");
    } catch (err) { toast.error(errMsg(err, "Message not sent")); }
  };
  const input = "w-full h-10 px-3 rounded-md bg-[#08090C] border border-white/10 text-sm focus:outline-none focus:border-emerald-500/60";

  if (isAdmin || cartOpen) return null;
  return (
    <div data-testid="chat-widget" className={`fixed right-4 lg:right-5 z-[60] lg:bottom-5 bottom-[calc(5rem+env(safe-area-inset-bottom))] ${payPage && !open ? "hidden lg:block" : ""}`}>
      {open && (
        <div data-testid="chat-panel" className="mb-3 w-[calc(100vw-2rem)] sm:w-96 h-[480px] max-h-[65vh] lg:max-h-[70vh] rounded-xl border border-white/10 bg-[#0F1117]/95 backdrop-blur-xl shadow-2xl flex flex-col overflow-hidden rise">
          <div className="flex items-center justify-between px-4 h-14 border-b border-white/10">
            <div><p className="font-display font-semibold text-sm">Chat with us</p><p className="text-xs text-emerald-400">We usually reply within minutes</p></div>
            <div className="flex items-center">
              <a data-testid="chat-header-telegram" href={TELEGRAM_URL} target="_blank" rel="noopener noreferrer" aria-label="Chat on Telegram" className="p-2 text-sky-400 hover:text-sky-300"><Send size={17} /></a>
              <button data-testid="chat-close-button" onClick={() => setOpen(false)} className="p-2 text-gray-400 hover:text-white"><X size={18} /></button>
            </div>
          </div>
          {!chat ? (
            <form onSubmit={begin} className="p-4 space-y-3 flex-1">
              <p className="text-sm text-gray-400">Questions about a key, a payment or an order? Start a chat.</p>
              <input data-testid="chat-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" required maxLength={80} className={input} />
              <input data-testid="chat-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email" required className={input} />
              <button data-testid="chat-start-button" disabled={starting} className="w-full h-10 rounded-md bg-emerald-500 text-[#08090C] font-bold hover:bg-emerald-400 disabled:opacity-50">{starting ? "Starting..." : "Start chat"}</button>
              <a data-testid="chat-telegram-link" href={TELEGRAM_URL} target="_blank" rel="noopener noreferrer"
                className="w-full h-10 rounded-md border border-sky-500/40 text-sky-300 text-sm font-semibold inline-flex items-center justify-center gap-2 hover:border-sky-400 transition-colors"><Send size={14} /> Prefer Telegram? Message @Randal782</a>
            </form>
          ) : (
            <>
              <div data-testid="chat-messages" className="flex-1 overflow-y-auto p-4 space-y-2">
                {msgs.length === 0 && <p className="text-sm text-gray-500 text-center pt-10">Say hi — include your order ID if you have one.</p>}
                {msgs.map((m) => <Bubble key={m.id} m={m} mine={m.sender === "customer"} />)}
                <div ref={endRef} />
              </div>
              <form onSubmit={send} className="p-3 border-t border-white/10 flex gap-2">
                <input data-testid="chat-message-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message..." className={input} />
                <button data-testid="chat-send-button" className="w-10 h-10 shrink-0 grid place-items-center rounded-md bg-emerald-500 text-[#08090C] hover:bg-emerald-400"><Send size={16} /></button>
              </form>
            </>
          )}
        </div>
      )}
      <div className="flex justify-end">
        <button data-testid="chat-open-button" onClick={() => setOpen(!open)} aria-label="Open chat"
          className="w-12 h-12 lg:w-14 lg:h-14 rounded-full bg-emerald-500 text-[#08090C] grid place-items-center shadow-[0_0_30px_rgba(16,185,129,0.4)] hover:bg-emerald-400 hover:scale-105 transition-[transform,background-color]">
          {open ? <X size={22} /> : <MessageCircle size={24} />}
        </button>
      </div>
    </div>
  );
};
